export const en = {
  app: {
    name: 'Sudoku',
    title: 'Sudoku',
  },
  seo: {
    title:
      'Free Sudoku Online — Easy to Hard, with Hints | BN-games',
    description:
      'Play free sudoku in your browser — fresh 9×9 puzzles in three difficulty levels, pencil marks, undo, and hints that explain the next logical step.',
    social:
      'Free 9×9 sudoku in your browser — three difficulty levels and hints that explain themselves.',
    h1:
      'Free sudoku online',
    intro:
      'Sudoku is a logic puzzle on a 9×9 grid: fill every row, column and 3×3 box with the digits 1 to 9, each exactly once. It needs no arithmetic — only logic.\n\nBN-games generates a fresh puzzle every game in three difficulty levels. Pencil marks, undo and redo are built in, and a hint shows the next logical step with an explanation, so it teaches the technique instead of just filling in a cell. It is free, needs no account, and is also available as an app for iOS and Android.',
    rulesTitle:
      'Sudoku Rules — How to Play Sudoku | BN-games',
    rulesDescription:
      'How to play sudoku: the rules of the classic 9×9 puzzle in plain words — then play a fresh puzzle free online, with hints.',
    rulesH1:
      'Sudoku rules: how to play',
    playCta:
      'Play sudoku now',
  },
  common: {
    back: '← Back',
    cancel: 'Cancel',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    google: 'Google',
    github: 'GitHub',
    completeProfile: 'Complete your profile →',
    profile: 'Profile',
    backToLanding: '← BN-games',
  },
  social: {
    close: 'Close',
    invite: {
      message: '{{sender}} invites you to {{game}}',
      moreCount_one: '+{{count}} more invite',
      moreCount_few: '+{{count}} more invites',
      moreCount_many: '+{{count}} more invites',
      moreCount_other: '+{{count}} more invites',
      accept: 'Accept',
      decline: 'Decline',
      acceptAria: "Accept {{sender}}'s {{game}} invite",
      declineAria: "Decline {{sender}}'s {{game}} invite",
    },
    dock: {
      pillLabel: 'From {{sender}}',
      pillAria: 'Invite from {{sender}} to {{game}} — show',
      popoverLabel: 'Invite',
      close: 'Close invite',
      leaveGameTitle: 'Leave this game?',
      leaveGameBody: 'Accepting opens {{game}} and leaves the current game. In an online game your seat is held for a while, so you can come back.',
      leaveGameConfirm: 'Leave and join',
      leaveGameCancel: 'Stay',
    },
    friendPicker: {
      button: 'Invite friend',
      modalTitle: 'Invite a friend',
      loading: 'Loading friends…',
      signInPrompt: 'Sign in to invite a friend.',
      empty: "You don't have any friends yet.",
      findFriends: 'Find friends',
      invite: 'Invite',
      inviteAria: 'Invite {{name}}',
      invited: 'Invited',
      invitedAria: 'Invited {{name}}',
      notAccepting: "This player isn't accepting invites right now",
      retryIn: 'Try again in {{seconds}}s',
    },
    addOpponent: {
      buttonMany: 'Add friends…',
      modalTitle: 'Add friends',
      add: 'Add {{name}} as friend',
      added: 'Added',
      addedAria: 'Added {{name}} as friend',
    },
  },
  // Sudoku is solo-only and never opens this modal — the keys exist purely
  // because @bn-games/ui-shell's ResumeFailureModal (M6) is barrel-exported
  // and its literal t() calls are typechecked against every consuming app's
  // dictionary, same reason `social.addOpponent.*` above is carried unused.
  online: {
    resumeModal: {
      seatTakenTitle: 'Seat taken',
      seatTakenBody: 'That seat belongs to another account. Sign in with that account, or start a new game.',
      roomGoneTitle: 'Table closed',
      roomGoneBody: 'This game is over — the table has closed.',
      roomFullTitle: 'Room full',
      roomFullBody: 'Every seat at this table is taken, and none of them is yours.',
      anonymousTitle: "We can't confirm this seat is yours",
      anonymousBody: 'This browser has no record of the game. Sign in before your next online game and your seat follows you to any device.',
      unreachableTitle: "Couldn't reach the server",
      unreachableBody: "The server didn't answer. Check your connection and try again.",
      signIn: 'Sign in',
      retry: 'Try again',
    },
  },
  mainMenu: {
    quickStart: 'Quick Start',
    newGame: 'New Puzzle',
    rules: 'Rules',
    settings: 'Settings',
    history: 'Games History',
    about: 'About',
  },
  play: {
    title: 'New Puzzle',
    startButton: 'Start',
    resume: 'Resume',
    difficulty: {
      easy: 'Easy',
      medium: 'Medium',
      hard: 'Hard',
    },
  },
  settings: {
    title: 'Settings',
    difficulty: 'Default difficulty',
    highlightErrors: 'Highlight errors',
    theme: 'Theme',
    language: 'Language',
    back: '← Back',
    tabs: {
      gameplay: 'Gameplay',
      interface: 'Interface',
    },
    difficulties: {
      easy: 'Easy',
      medium: 'Medium',
      hard: 'Hard',
    },
    themes: {
      mahogany: 'Mahogany',
      persian: 'Persian',
      deco: 'Deco',
      monte: 'Monte',
    },
    languages: {
      en: 'English',
      ru: 'Русский',
    },
  },
  rules: {
    title: 'Rules',
    body: 'Sudoku is a logic puzzle played on a 9×9 grid divided into nine 3×3 boxes. Some cells are filled with digits from 1 to 9; the rest are blank.\n\nYour goal is to fill every blank cell so that each row, each column, and each 3×3 box contains the digits 1 through 9 exactly once. There is always a single unique solution that can be reached by pure logic — no guessing required.',
  },
  about: {
    title: 'About',
    body: 'A browser-based Sudoku built with TypeScript and HTML5 Canvas. v1 ships three difficulty tiers, pencil marks, undo/redo, hints with technique names, error highlighting, a pause-aware timer, shareable puzzle links, and persistent history with replay.',
  },
  history: {
    title: 'Game history',
    empty: 'No saved games yet — solve a puzzle to start filling this list.',
    replayButton: 'Replay',
    column: {
      completedAt: 'Completed',
      difficulty: 'Difficulty',
      time: 'Time',
      hints: 'Hints',
      errors: 'Errors',
    },
  },
  historyList: {
    justNow: 'just now',
    minutesAgo: '{{count}} min ago',
    hoursAgo: '{{count}} h ago',
    daysAgo: '{{count}} d ago',
    hintsCount: '{{count}} hints',
    errorsCount: '{{count}} errors',
    deleteAria: 'Delete this record',
    deleteConfirm: 'Delete this record?',
    clearAll: 'Clear all',
    clearAllConfirm: 'Clear all history?',
    clearAllBody: 'This will remove every finished puzzle from your history. The action cannot be undone.',
    forfeitedTag: 'Revealed',
  },
  game: {
    timer: {
      label: 'Time',
    },
    stats: {
      game: 'Game',
      errors: 'Errors',
    },
    hint: {
      button: 'Hint',
    },
    pencil: {
      button: 'Notes',
    },
    erase: {
      button: 'Erase',
    },
    undo: {
      button: 'Undo',
    },
    redo: {
      button: 'Redo',
    },
    win: {
      banner: 'Solved!',
    },
    a11y: {
      canvasLabel: 'Sudoku board. Use the arrow keys to move the selected cell, 1 through 9 to enter digits, Backspace to erase, P to toggle pencil mode, Z to undo, Y to redo, Escape to pause.',
      cellAnnouncement: 'Row {{row}}, column {{col}}, {{value}}.',
      cellEmpty: 'empty',
      cellGiven: 'given {{digit}}',
      cellEntered: 'entered {{digit}}',
      hintAnnouncement: '{{technique}}: focus on row {{row}}, column {{col}}.',
      errorAnnouncement: 'Wrong digit.',
    },
    end: {
      title: 'Puzzle solved!',
      subtitle: 'Difficulty: {{difficulty}}',
      timeLabel: 'Time',
      hintsLabel: 'Hints',
      errorsLabel: 'Errors',
      playAgain: 'Play again',
      backToMenu: 'Back to menu',
      a11yAnnounce: 'Sudoku solved in {{time}}. {{hints}} hints used, {{errors}} errors made.',
      forfeitedTitle: 'Solution revealed',
      forfeitedSubtitle: 'You gave up on this puzzle.',
      viewSolution: 'View board',
    },
  },
  pause: {
    title: 'Paused',
    resume: 'Resume',
    settings: 'Settings',
    share: 'Share puzzle',
    restart: 'Restart',
    quit: 'Return to menu',
    solve: 'Reveal solution',
    solveConfirm: {
      title: 'Reveal the solution?',
      message: 'This will end the puzzle and record it as forfeited in your history.',
      confirm: 'Reveal',
      cancel: 'Cancel',
    },
  },
  share: {
    modal: {
      title: 'Share this puzzle',
      copyButton: 'Copy link',
      copied: 'Copied!',
      systemShareButton: 'Share…',
    },
    banner: {
      text: 'Shared by {{from}} — solved in {{time}}.',
    },
  },
  hint: {
    technique: {
      'naked-single': 'Naked single',
      'hidden-single': 'Hidden single',
      'naked-pair': 'Naked pair',
      'pointing-pair': 'Pointing pair',
      'box-line': 'Box/line reduction',
      'x-wing': 'X-wing',
    },
  },
  error: {
    shareLink: {
      invalid: 'That share link looks malformed. Double-check the URL.',
      outdated: 'That share link is from an older version and can no longer be opened.',
      generic: "Couldn't open the shared puzzle. Try asking for a fresh link.",
    },
    unknown: 'Something went wrong. Please try again.',
  },
  replay: {
    controls: {
      first: 'First',
      previous: 'Previous',
      play: 'Play',
      pause: 'Pause',
      next: 'Next',
      last: 'Last',
      speed: 'Speed',
    },
  },
  pwa: {
    updateAvailable: {
      title: 'Update available',
      body: 'A new version of Sudoku is ready. Refresh to load it.',
      refresh: 'Refresh',
      later: 'Later',
    },
  },
};
